/* jshint esversion: 6, -W097 */
/* globals module, require */
'use strict';

/**
 * The DataCollectionDirective displays a dismissable banner with text pulled
 * from the CMS. When dismissed the banner will stay hidden until the text
 * is updated in the CMS or its related cookie expires.
 *
 * It it intended to be used to give the the user important information about
 * the website tracking their data.
 *
 * @module enervee_utils.user_data_collection_alert
 */
function UserConsentAlertDirective() {
    return {
        // must be an element (can not be used as an attribute or ...)
        scope: {
            alertType: '@',
            alertSize: '@',
        },
        restrict: 'E',
        controller: UserConsentAlertController,
        bindToController: true,
        controllerAs: 'vm',
        template: require('./userConsentAlertView.html'),
    };
}

UserConsentAlertController.$inject = [
    'CmsContentService',
    'CookiesService',
    'UtilService',
];
function UserConsentAlertController(
    CmsContentService,
    CookiesService,
    UtilService
) {
    this.$onInit = () => {
        const vm = this;
        const alertEnabled = UtilService.getSiteSetting(`${vm.alertType}_alert_enabled`, false);
        vm.onDismissAlert = onDismissAlert;
        // We'll set this to false by default, and switch it to true if a message
        // needs to be shown.
        vm.showAlert = false;
        vm.cookieName = '';
        vm.hash = '';
        vm.cmsQueries = {};
        vm.cmsTexts = {};

        setupAlert();

        /**
        * Retrieves CMS texts and sets up alert cookie.
        */
        function setupAlert() {
            vm.cookieName = createCookieName(vm.alertType);
            vm.cmsQueries = createCMSQueries();
            if (alertEnabled) {
                getAlertTextFromCMS()
                    // Add the hashed value of all text combined to the array.
                    .then(alertTextArray => {
                        return [
                            ...alertTextArray,
                            UtilService
                                .hashString(alertTextArray.join(''))
                                .toString(),
                        ];
                    })
                    // Set vm variables and CMS texts
                    .then(([message, link, button, hash]) => {
                        vm.cmsTexts = { message, link, button };
                        vm.hash = hash;
                        vm.showAlert = shouldAlertBeShown(
                            hash,
                            CookiesService.get(vm.cookieName),
                        );
                    });
            }
        }

        /**
        * Creates new string from argument string that uses underscores
        * instead of hyphens, and then creates a cookie name with it.
        *
        * @param {string} str - String to be used in cookie name.
        * @returns {string} Full cookie name.
        */
        function createCookieName(str) {
            str = str.split('_').join('-');
            return `enervee-${str}-alert-text-hash`;
        }

        /**
        * Creates CMS query strings and adds them to the vm.cmsQueries object.
        */
        function createCMSQueries() {
            const baseStr = `base:${vm.alertType}_alert:${vm.alertType}_alert`;
            return {
                message: baseStr + '_text',
                link: baseStr + '_link_text',
                button: baseStr + '_button_text',
            };
        }

        /**
        * Handles the logic when dismissing the cookie alert.
        */
        function onDismissAlert() {
            CookiesService.set(vm.cookieName, vm.hash);
        }

        /**
        * Creates multiple promises from all cms query strings in vm.cmsQueries
        * to retrieve the alert text from the CMS and combines them into a single promise.
        * If a request fails, we default that value to an empty string.
        *
        * @returns {Promise} A promise that fulfills with an array of multiple strings
        */
        function getAlertTextFromCMS() {
            const defaultToEmptyString = () => "";
            // Create array of promises from query object keys.
            const cmsPromises = Object
                .keys(vm.cmsQueries)
                .map(key => (
                    CmsContentService
                        .getMarketPlaceText(vm.cmsQueries[key])
                        .catch(defaultToEmptyString)
                ));
            return Promise.all(cmsPromises);
        }

        /**
        * Determines whether or not the alert banner should be displayed.
        *
        * @param {String} alertTextHash A unique hash of the text to display in
        *                               the banner.
        * @param {String} previousTextHash The hash of the text previously
        *                                  displayed on the site.
        */
        function shouldAlertBeShown(alertTextHash, previousTextHash) {
            // If the text hash is not an empty string and the hash does not match
            // the previous value, then we have a new message that the user has not
            // dismissed. Let's display the message.
            return alertTextHash !== '0' && alertTextHash !== previousTextHash;
        }
    }
}

module.exports = UserConsentAlertDirective;
